import { FC } from "react";

import { MappedStudentAssessmentScoreTableRow } from "./utils";

import { ScrollableTableContainer } from "components/tables/ScrollableTableContainer";
import { TD, TH, THead, TR, Table } from "components/tables/TableElements";
import { t } from "i18n/i18n";
import { Tooltip } from "mds/components/Tooltip";
import { CommentIcon, EyeNotVisibleIcon } from "mds/icons";
import { useSectionCapabilitiesBySectionId } from "store/capability-hooks";
import { Link } from "utils/routing";
import { pageUrl } from "utils/urls";

type StudentScoreOverviewTableProps = {
  sectionId: string;
  tableRows: MappedStudentAssessmentScoreTableRow[];
};

export const StudentScoreOverviewTable: FC<StudentScoreOverviewTableProps> = ({
  sectionId,
  tableRows,
}) => {
  const { canParticipateInSection } = useSectionCapabilitiesBySectionId(sectionId);

  if (tableRows.length === 0) {
    return null;
  }

  return (
    <ScrollableTableContainer
      className="w-full overflow-hidden"
      title={t("assessment_dashboard.all.title")}
    >
      <Table>
        {/* Headers: Page / Page Type / Points / Outcomes / Submitted At */}
        <THead>
          <TR className="border-black-tint-70">
            <TH className="pl-4">{t("assessment_dashboard.all.page_topic_header_title")}</TH>

            <TH>{t("assessment_dashboard.all.page_header_title")}</TH>

            <TH>
              <Tooltip text={t("assessment_dashboard.all.assessment_category_header_tooltip")}>
                {t("assessment_dashboard.all.assessment_category_header_title")}
              </Tooltip>
            </TH>

            <TH>{t("assessment_dashboard.student.points_header_title")}</TH>

            <TH>{t("assessment_dashboard.all.outcomes_header_title")}</TH>

            <TH>{t("assessment_dashboard.student.submitted_header_title")}</TH>
          </TR>
        </THead>

        <tbody>
          {tableRows.map((row) => (
            <TR key={row.id}>
              <TD className="max-w-52 truncate py-2.5 pl-[17px]" title={row.topicTitle || "–"}>
                {row.topicTitle || "–"}
              </TD>

              <TD className="w-52" title={row.title}>
                <div className="flex max-w-52 items-center gap-2">
                  <div className="min-w-0">
                    {row.hidden && canParticipateInSection ? (
                      <span className="body-xs flex w-full items-center gap-1">
                        <Tooltip text={t("assessment_dashboard.all.hidden_page_tooltip")}>
                          <EyeNotVisibleIcon className="inline shrink-0 text-black-tint-40" />
                        </Tooltip>

                        <span className="truncate">{row.title}</span>
                      </span>
                    ) : (
                      <Link
                        className="body-xs block flex w-full items-center gap-1 text-blue-shade-20 underline"
                        to={pageUrl(sectionId, row.id)}
                      >
                        {row.hidden && (
                          <Tooltip text={t("assessment_dashboard.all.hidden_page_tooltip")}>
                            <EyeNotVisibleIcon className="inline shrink-0 text-black-tint-40" />
                          </Tooltip>
                        )}

                        <span className="truncate">{row.title}</span>
                      </Link>
                    )}
                  </div>

                  {row.assessmentComments.length > 0 && (
                    <div className="flex shrink-0 items-center gap-1">
                      {row.assessmentComments.map((assessmentComment) => (
                        <Tooltip
                          key={assessmentComment.assessment_id}
                          text={
                            <div className="body-xs text-black-tint-40">
                              {assessmentComment.comment}
                            </div>
                          }
                          wrapperClassName="inline-flex shrink-0"
                        >
                          <CommentIcon
                            aria-label={t("assessment.instructor_comment")}
                            className="shrink-0 text-blue-shade-40"
                            size="l"
                          />
                        </Tooltip>
                      ))}
                    </div>
                  )}
                </div>
              </TD>

              <TD>{row.assessmentCategory}</TD>

              <TD>{row.points}</TD>

              <TD>
                <Tooltip text={row.outcomesTooltip}>{row.outcomesCount.toString()}</Tooltip>
              </TD>

              <TD>{row.submittedAt}</TD>
            </TR>
          ))}
        </tbody>
      </Table>
    </ScrollableTableContainer>
  );
};
